import i18next from 'i18next';
import httpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import type { User } from 'src/shared/graphql/generated';
import { setCookie } from 'src/shared/utils/cookie';

import {
  DefaultLocale,
  SupportedLocales,
  baseConfiguration,
} from './baseI18nConfiguration';

export async function initBrowserI18n() {
  await i18next
    .use(initReactI18next)
    .use(httpBackend)
    .init({
      ...baseConfiguration,
      lng: window.lang,
      resources: window.i18nStore,
    });

  if (__DEV__) {
    import('./configureI18nHotReload');
  }

  return i18next;
}

export function syncAppLocale(user: User | undefined) {
  // Works only in the browser
  if (typeof window === 'undefined') {
    return;
  }

  // Ensure that a "locale" query parameter is set in the URL has priority over the cookie
  // This allow us to use the zz-ZZ testing locale without changing the user parameter
  // but also to impersonate a user and switch to a different language than their settings
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (urlSearchParams.has('locale')) {
    return;
  }

  const userLocale = user?.locale;
  if (!userLocale) {
    return;
  }

  let locale = DefaultLocale;

  // We expect the backend to return a locale in the format "EN_US"
  if (userLocale.length === 5) {
    // "EN_US" is not a BCP-47 format, and must be converted to "en-US"
    // The value is stored as "en-US" in the db, but GQL does not like the "-" in the enum
    // See https://github.com/hoverinc/hyperion/blob/37634fdc2bac49aad4a346a0aa8c9d60cf8648e8/app/graphql/types/enums/locale.rb#L6
    locale = `${userLocale.slice(0, 2).toLowerCase()}-${userLocale
      .slice(3)
      .toUpperCase()}`;
  }

  // We do not recognize the locale, let's ignore it
  if (!SupportedLocales.includes(locale)) {
    return;
  }
  setCookie('locale', locale);

  // Mismatch of what the user expect and what the page is returning
  // Reload the page so that the correct lang is loaded
  if (locale !== window.lang) {
    // window.lang is set on the server, using the cookie
    document.location.reload();
  }
}
